import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTestCycles, deleteTestCycle, prefetchTestCycleDetails } from '../services/testCycleService';
import TestCycleWizard from '../components/TestCycleWizard';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Alert,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { useTestCycleExecution } from '../context/TestCycleExecutionContext';
import { apiService } from '../services/apiService';
import { formatAppTimestamp } from '../utils/dateTimeUtils';
import { PROJECT_SELECT_CONTROL_WIDTH } from './Projects';
import { DFT_BRAND, zoomedMainMinHeight } from '../styles/brandPalette';

const TEST_PLANS_CARD_RADIUS = '14px';
const testPlansPageSx = {
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  alignSelf: 'stretch',
  flex: 1,
  px: 0,
  py: 0,
  bgcolor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
  boxSizing: 'border-box' as const,
  minHeight: {
    xs: 'calc(100dvh - 64px)',
    ...zoomedMainMinHeight(),
  },
  '& .MuiCard-root': {
    borderRadius: `${TEST_PLANS_CARD_RADIUS} !important`,
    overflow: 'hidden',
  },
  '& .MuiAlert-root': {
    borderRadius: '12px',
  },
  '& .MuiTypography-subtitle2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  '& .MuiTypography-body2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
} as const;
const TEST_PLANS_WORKSPACE_SX = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '0 12px 12px 12px',
  border: `1px solid ${DFT_BRAND.line}`,
  borderTop: `1px solid ${DFT_BRAND.line}`,
  bgcolor: DFT_BRAND.bg,
  boxShadow: 'none',
  overflow: 'visible',
  p: { xs: 1.25, sm: 1.5 },
} as const;
const testPlansCardSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative' as const,
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: `${TEST_PLANS_CARD_RADIUS} !important`,
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
} as const;
const testPlansMetricCardSx = {
  ...testPlansCardSx,
  height: '100%',
  '&::before': {
    content: '""',
    position: 'absolute',
    height: 3,
    left: 0,
    right: 0,
    top: 0,
    background: DFT_BRAND.metricAccent,
    borderRadius: `${TEST_PLANS_CARD_RADIUS} ${TEST_PLANS_CARD_RADIUS} 0 0`,
  },
} as const;

const DEFAULT_MAX_TEST_PLANS = 100;
const TEST_PLAN_LIMIT_REACHED_MESSAGE = 'Test plan limit reached. Upgrade plan or delete existing.';

/** MUI Select menus in this layout can misalign under CSS zoom; anchor to viewport positions like Collections. */
function useZoomSafeSelectMenu(extra?: {
  paperSx?: Record<string, unknown>;
  menuListSx?: Record<string, unknown>;
}) {
  const [menuState, setMenuState] = useState({
    open: false,
    top: 0,
    left: 0,
  });

  const onOpen = (e: React.SyntheticEvent) => {
    const el = e.currentTarget as HTMLElement;
    const combobox =
      (el.matches('[role="combobox"]') ? el : el.querySelector<HTMLElement>('[role="combobox"]')) ?? el;
    const rect = combobox.getBoundingClientRect();
    setMenuState({
      open: true,
      top: rect.bottom + 4,
      left: rect.left,
    });
  };

  const onClose = () => setMenuState((prev) => ({ ...prev, open: false }));

  const MenuProps = {
    disableScrollLock: true,
    anchorReference: 'anchorPosition' as const,
    anchorPosition: { top: menuState.top, left: menuState.left },
    transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
    sx: { zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal },
    PaperProps: {
      sx: {
        mt: 0.5,
        zIndex: (theme: import('@mui/material/styles').Theme) => theme.zIndex.modal,
        ...extra?.paperSx,
      },
    },
    MenuListProps: {
      sx: {
        maxHeight: 320,
        overflowY: 'auto' as const,
        py: 0.5,
        ...(extra?.menuListSx ?? {}),
      },
    },
  };

  return { menuOpen: menuState.open, onOpen, onClose, MenuProps };
}

const TestCycles: React.FC = () => {
  const navigate = useNavigate();
  const { projects, selectedProject, setSelectedProject, isLoading: projectsLoading, error: projectsError, refreshProjects } = useProjects();
  const { isAuthenticated } = useAuth();
  const { activeExecution } = useTestCycleExecution();
  const executingCycleId =
    activeExecution?.executing && activeExecution.cycleId > 0 ? activeExecution.cycleId : null;
  const [cycles, setCycles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingCycleId, setEditingCycleId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [deletingCycleId, setDeletingCycleId] = useState<number | null>(null);

  const [maxTestSuites, setMaxTestSuites] = useState<number>(DEFAULT_MAX_TEST_PLANS);
  const [testPlanLimitsLoaded, setTestPlanLimitsLoaded] = useState(false);
  const testPlanProjectSelectRef = useRef<HTMLDivElement>(null);
  const [testPlanProjectMenuWidth, setTestPlanProjectMenuWidth] = useState(0);

  const projectZoomMenu = useZoomSafeSelectMenu({
    paperSx:
      testPlanProjectMenuWidth > 0
        ? {
            width: `${testPlanProjectMenuWidth}px !important`,
            minWidth: `${testPlanProjectMenuWidth}px !important`,
            maxWidth: `${testPlanProjectMenuWidth}px !important`,
          }
        : undefined,
    menuListSx: {
      '& .MuiMenuItem-root': {
        minHeight: 40,
        whiteSpace: 'nowrap',
        overflow: 'visible',
      },
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
  });

  const syncTestPlanProjectMenuPaperWidth = (paperEl: HTMLElement) => {
    const width = testPlanProjectSelectRef.current?.getBoundingClientRect().width ?? 0;
    if (width > 0) {
      const w = `${Math.round(width)}px`;
      paperEl.style.width = w;
      paperEl.style.minWidth = w;
      paperEl.style.maxWidth = w;
      setTestPlanProjectMenuWidth(Math.round(width));
    }
  };

  const refreshTestPlanLimits = React.useCallback(() => {
    return apiService
      .getSubscriptionDetails()
      .then((details) => {
        const limit = details?.limits?.maxTestSuites;
        if (typeof limit === 'number' && limit > 0) {
          setMaxTestSuites(limit);
        }
        setTestPlanLimitsLoaded(true);
      })
      .catch(() => {
        setTestPlanLimitsLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      void refreshTestPlanLimits();
    }
  }, [isAuthenticated, refreshTestPlanLimits]);

  const isTestPlanLimitReached =
    testPlanLimitsLoaded &&
    Boolean(selectedProject?.id) &&
    cycles.length >= maxTestSuites;

  const DASHBOARD_RECENTS_EVENT = 'dashboard-recent-updated';

  const fetchCycles = React.useCallback(async (projectId: number) => {
    if (!isAuthenticated) {
      setError('Please log in to view test plans');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await getTestCycles(projectId);
      setCycles(Array.isArray(res) ? res : []);
    } catch (err: any) {
      if (err.message === 'Network Error' || err.code === 'ERR_NETWORK' || err.code === 'ERR_CORS') {
        if (err.response === undefined && err.request !== undefined) {
          setError('CORS error or network issue. Please check backend CORS configuration and that it is running on http://localhost:8080.');
        } else {
          setError('Network error: Could not connect to the server. Please check if the backend is running on http://localhost:8080');
        }
      } else if (err.response?.status === 403 || err.response?.status === 401) {
        setError('Authentication required. Please log in again.');
      } else if (err.response?.status) {
        setError(`Failed to load test plans: ${err.response?.data?.message || err.message} (Status: ${err.response?.status})`);
      } else {
        setError(`Failed to load test plans: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setMessage(null);
    if (selectedProject && isAuthenticated) {
      fetchCycles(selectedProject.id);
    } else {
      setCycles([]);
      if (!isAuthenticated) {
        setError('Please log in to view test plans');
      } else if (!selectedProject) {
        setError('Please select a project to view test plans');
      }
    }
  }, [selectedProject, isAuthenticated, fetchCycles]);

  const handleDeleteRequest = (cycle: { id: number; name?: string }) => {
    if (!selectedProject || !cycle?.id) return;
    if (executingCycleId === cycle.id) return;
    setDeleteTarget({ id: cycle.id, name: cycle.name || 'this test plan' });
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProject || !deleteTarget) return;
    const cycleId = deleteTarget.id;
    setDeletingCycleId(cycleId);
    setError(null);
    try {
      await deleteTestCycle(selectedProject.id, cycleId);
      setCycles((prev) => prev.filter((c) => c.id !== cycleId));
      setMessage({ text: 'Test plan deleted successfully', type: 'success' });
      setDeleteTarget(null);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(DASHBOARD_RECENTS_EVENT));
      }
      void refreshTestPlanLimits();
    } catch (err: any) {
      setError(`Failed to delete test plan: ${err.response?.data?.error || err.response?.data?.message || err.message}`);
    } finally {
      setDeletingCycleId(null);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCycles = cycles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openTestPlan = (cycleId: number) => {
    if (!selectedProject?.id) return;
    prefetchTestCycleDetails(selectedProject.id, cycleId);
    navigate(`/test-plans/${cycleId}?projectId=${selectedProject.id}`);
  };

  return (
    <Box sx={testPlansPageSx}>
      <Box sx={{ width: '100%', flexShrink: 0, overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1,
            minWidth: 0,
            px: { xs: 1.25, sm: 1.5 },
            pt: 1.25,
            pb: 0.5,
            minHeight: 36,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              m: 0,
              fontWeight: 700,
              height: 36,
              lineHeight: '36px',
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '-0.02em',
              color: DFT_BRAND.text,
            }}
          >
            Test Plans
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minHeight: 36,
              '& .MuiButton-root': {
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 1,
              },
              '& .MuiButton-startIcon': {
                display: 'inline-flex',
                alignItems: 'center',
                marginTop: 0,
                marginBottom: 0,
              },
            }}
          >
          <FormControl
            ref={testPlanProjectSelectRef}
            size="small"
            variant="outlined"
            sx={{
              width: { xs: 180, sm: 220, md: 240, lg: PROJECT_SELECT_CONTROL_WIDTH.sm },
              flexShrink: 0,
              alignSelf: 'center',
              overflow: 'visible',
              '& .MuiInputLabel-root': {
                backgroundColor: DFT_BRAND.paper,
                px: 0.5,
                lineHeight: 1.25,
                zIndex: 1,
              },
              '& .MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
              },
              '& .MuiOutlinedInput-root': {
                height: 36,
                minHeight: 36,
                backgroundColor: DFT_BRAND.paper,
                borderRadius: '10px',
              },
            }}
          >
            <InputLabel id="test-plan-project-select-label" shrink>
              Project
            </InputLabel>
            <Select
              labelId="test-plan-project-select-label"
              id="test-plan-project-select"
              value={selectedProject ? selectedProject.id : ''}
              label="Project"
              open={projectZoomMenu.menuOpen}
              onOpen={(e) => {
                const width = testPlanProjectSelectRef.current?.getBoundingClientRect().width ?? 0;
                if (width > 0) setTestPlanProjectMenuWidth(Math.round(width));
                projectZoomMenu.onOpen(e);
              }}
              onClose={projectZoomMenu.onClose}
              MenuProps={projectZoomMenu.MenuProps}
              sx={{
                width: '100%',
                bgcolor: DFT_BRAND.paper,
                '& .MuiSelect-select': {
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  lineHeight: 1.35,
                  display: 'block',
                  boxSizing: 'border-box',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
              onChange={(e) => {
                const projectId = Number(e.target.value);
                const project = projects.find((p) => p.id === projectId);
                setSelectedProject(project || null);
              }}
            >
              {projectsLoading ? (
                <MenuItem disabled value="">
                  <CircularProgress size={20} /> Loading...
                </MenuItem>
              ) : (
                projects.map((project) => (
                  <MenuItem
                    key={project.id}
                    value={project.id}
                    title={project.name}
                    sx={{ fontSize: '0.875rem', py: 1 }}
                  >
                    {project.name}
                  </MenuItem>
                ))
              )}
              {projects.length === 0 && !projectsLoading && (
                <MenuItem disabled>No projects found</MenuItem>
              )}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setWizardOpen(true)}
            disabled={!selectedProject || isTestPlanLimitReached}
            title={
              isTestPlanLimitReached
                ? TEST_PLAN_LIMIT_REACHED_MESSAGE
                : undefined
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              height: 36,
              minHeight: 36,
              borderRadius: 1.5,
              bgcolor: '#1d4ed8',
              '&:hover': { bgcolor: '#1e40af' },
              px: 1.2,
              fontSize: '0.875rem',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Create Test Plan
          </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={TEST_PLANS_WORKSPACE_SX}>
      {!loading && cycles.length > 0 && (
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <Card elevation={0} sx={testPlansMetricCardSx}>
              <CardContent sx={{ p: '12px !important' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: DFT_BRAND.text, mb: 0.5 }}>
                  {cycles.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Test Plans
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontSize: '0.75rem' }}>
                  Showing {paginatedCycles.length} on page {page + 1}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Card elevation={0} sx={testPlansMetricCardSx}>
              <CardContent sx={{ p: '12px !important' }}>
                <Typography
                  title={selectedProject?.name || 'N/A'}
                  sx={{
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: DFT_BRAND.text,
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedProject?.name || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current Project
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Card elevation={0} sx={testPlansMetricCardSx}>
              <CardContent sx={{ p: '12px !important' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: DFT_BRAND.text, mb: 0.5 }}>
                  {projects.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Projects
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Card elevation={0} sx={testPlansMetricCardSx}>
              <CardContent sx={{ p: '12px !important' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: DFT_BRAND.text, mb: 0.5 }}>
                  {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Today
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {projectsError && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
          action={
            <Button size="small" onClick={refreshProjects} color="inherit">
              Retry
            </Button>
          }
        >
          {projectsError}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity={message.type} sx={{ mb: 1.5 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {!selectedProject && !loading && (
        <Card elevation={0} sx={{ ...testPlansCardSx, p: 2, textAlign: 'center' }}>
          <Alert severity="info">Please select a project to view test plans</Alert>
        </Card>
      )}

      {selectedProject && (
        <Card elevation={0} sx={testPlansCardSx}>
          <CardContent sx={{ width: '100%', maxWidth: '100%', minWidth: 0, p: '12px !important' }}>
            <TableContainer
              component={Paper}
              className="projects-table-container"
              sx={{
                boxShadow: 'none',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'auto',
                display: 'block',
              }}
            >
              <Table
                className="projects-table"
                sx={{
                  width: '100%',
                  minWidth: { xs: 520, md: '100%' },
                  maxWidth: '100%',
                  tableLayout: 'fixed',
                  display: 'table',
                  '& .MuiTableCell-root': {
                    fontSize: '0.875rem',
                    lineHeight: 1.45,
                  },
                }}
              >
                <colgroup>
                  <col />
                  <col />
                  <col />
                  <col style={{ width: 120 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: DFT_BRAND.text, borderColor: DFT_BRAND.line, bgcolor: DFT_BRAND.tint, py: 1 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: DFT_BRAND.text, borderColor: DFT_BRAND.line, bgcolor: DFT_BRAND.tint, py: 1, display: { xs: 'none', md: 'table-cell' } }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: DFT_BRAND.text, borderColor: DFT_BRAND.line, bgcolor: DFT_BRAND.tint, py: 1, display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        color: DFT_BRAND.text,
                        borderColor: DFT_BRAND.line,
                        bgcolor: DFT_BRAND.tint,
                        py: 1,
                        width: 120,
                        minWidth: 120,
                        maxWidth: 120,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && cycles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Loading test plans...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : cycles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                        <FolderIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1, display: 'block', mx: 'auto' }} />
                        <Typography variant="body2" color="text.secondary">
                          No test plans found. Create your first test plan to get started.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => setWizardOpen(true)}
                          sx={{
                            mt: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            height: 36,
                            minHeight: 36,
                            fontSize: '0.875rem',
                            bgcolor: '#1d4ed8',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#1e40af' },
                          }}
                        >
                          Create Test Plan
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedCycles.map((cycle) => {
                      const isPlanExecuting = executingCycleId === cycle.id;
                      return (
                      <TableRow
                        key={cycle.id}
                        onClick={() => openTestPlan(cycle.id)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: DFT_BRAND.paper,
                          '&:hover': { backgroundColor: DFT_BRAND.tint },
                          '& td': { borderColor: DFT_BRAND.line },
                        }}
                      >
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0, py: 1 }}>
                          <Typography
                            variant="subtitle2"
                            title={cycle.name}
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            onMouseEnter={() => selectedProject?.id && prefetchTestCycleDetails(selectedProject.id, cycle.id)}
                          >
                            {cycle.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: 0, py: 1, display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {cycle.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', py: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {formatAppTimestamp(cycle.createdAt ?? cycle.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            px: 0.5,
                            py: 1,
                            width: 120,
                            minWidth: 120,
                            maxWidth: 120,
                            whiteSpace: 'nowrap',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Box sx={{ display: 'flex', gap: 0.125, justifyContent: 'center', alignItems: 'center' }}>
                            <Tooltip title="Execute">
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label="Execute test plan"
                                sx={{ width: 32, height: 32, p: 0 }}
                                onClick={() => openTestPlan(cycle.id)}
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                isPlanExecuting
                                  ? 'Cannot edit while this test plan is executing'
                                  : 'Edit'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={isPlanExecuting}
                                  aria-label={
                                    isPlanExecuting
                                      ? `Edit disabled while test plan ${cycle.name} is executing`
                                      : `Edit test plan ${cycle.name}`
                                  }
                                  sx={{ width: 32, height: 32, p: 0 }}
                                  onClick={() => {
                                    if (isPlanExecuting) return;
                                    setEditingCycleId(cycle.id);
                                    setWizardOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                isPlanExecuting
                                  ? 'Cannot delete while this test plan is executing'
                                  : deletingCycleId === cycle.id
                                    ? 'Deleting…'
                                    : 'Delete'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={`Delete test plan ${cycle.name}`}
                                  data-testid={`delete-test-plan-${cycle.id}`}
                                  disabled={isPlanExecuting || deletingCycleId === cycle.id}
                                  sx={{ width: 32, height: 32, p: 0 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteRequest(cycle);
                                  }}
                                >
                                  {deletingCycleId === cycle.id ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {cycles.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={cycles.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  color: DFT_BRAND.muted,
                  borderTop: `1px solid ${DFT_BRAND.line}`,
                  overflow: 'hidden',
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    minHeight: 36,
                    px: 0.5,
                    gap: 0.5,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    color: DFT_BRAND.muted,
                    fontSize: '0.8125rem',
                    m: 0,
                  },
                  '& .MuiTablePagination-select': {
                    fontSize: '0.8125rem',
                  },
                  '& .MuiTablePagination-actions .MuiIconButton-root': {
                    p: 0.5,
                  },
                }}
              />
            )}
          </CardContent>
        </Card>
      )}
      </Box>

      {selectedProject && (
        <TestCycleWizard
          open={wizardOpen}
          onClose={() => {
            setWizardOpen(false);
            setEditingCycleId(null);
            void refreshTestPlanLimits();
            fetchCycles(selectedProject.id);
          }}
          projectId={selectedProject.id}
          cycleId={editingCycleId}
          testPlanCreationBlocked={!editingCycleId && isTestPlanLimitReached}
          testPlanCreationBlockedMessage={TEST_PLAN_LIMIT_REACHED_MESSAGE}
        />
      )}

      <Dialog
        open={deleteTarget != null}
        onClose={() => {
          if (deletingCycleId == null) setDeleteTarget(null);
        }}
        aria-labelledby="delete-test-plan-dialog-title"
        data-testid="delete-test-plan-dialog"
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 440 },
            maxWidth: 'min(92vw, 520px)',
            m: 2,
            borderRadius: '14px',
            border: `1px solid ${DFT_BRAND.line}`,
          },
        }}
      >
        <DialogTitle id="delete-test-plan-dialog-title">Delete test plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{deleteTarget?.name}&quot;? This permanently removes the plan and all its run history.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deletingCycleId != null}
            data-testid="delete-test-plan-cancel"
            sx={{ textTransform: 'none', fontWeight: 600, height: 36, fontSize: '0.875rem' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleDeleteConfirm()}
            color="error"
            variant="contained"
            disabled={deletingCycleId != null}
            data-testid="delete-test-plan-confirm"
            autoFocus
            sx={{ textTransform: 'none', fontWeight: 600, height: 36, fontSize: '0.875rem', boxShadow: 'none' }}
          >
            {deletingCycleId != null ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestCycles;
